import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { FaBarsStaggered } from "react-icons/fa6";
import axios from "axios";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";
import AdminNav from "./AdminNav";
import { currencyOptions, crptoOptionsFlag } from "./currencyOptions";
import { Select } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import SignLogo from "../../assets/images/logo-w.png";
const { Option } = Select;

const Transactions = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [status, setStatus] = useState("all");
  const [transactionId, setTransactionId] = useState("");
  const [loading, setLoading] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const token = localStorage.getItem("admintoken");

  // Fetch Transactions
  useEffect(() => {
    const fetchTransactionsData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API}/api/v1/transaction/transactions`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (data?.success) {
          const sortedTransactions = data.transactions.sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          );
          setAllTransactions(sortedTransactions);
          setFilteredTransactions(sortedTransactions);
        } else {
          toast.error(data.message || " No Transactions Found !");
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionsData();
  }, []);

  // Filter Logic
  useEffect(() => {
    let filtered = [...allTransactions];

    // Date Range Filter
    if (startDate && endDate) {
      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.date);
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1); // Move to the next day
        return (
          transactionDate >= new Date(startDate) &&
          transactionDate < adjustedEndDate
        );
      });
    }

    // Type Filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter((transaction) =>
        selectedTypes.includes(transaction.type)
      );
    }

    // Asset Filter
    if (selectedAssets.length > 0) {
      filtered = filtered.filter((transaction) =>
        selectedAssets.includes(transaction.currency || transaction.asset)
      );
    }

    // Status Filter
    if (status !== "all") {
      const statusValue = parseInt(status, 10);
      filtered = filtered.filter(
        (transaction) => transaction.status === statusValue
      );
    }

    // Transaction ID Search
    if (transactionId) {
      filtered = filtered.filter((transaction) =>
        transaction._id.includes(transactionId)
      );
    }

    setFilteredTransactions(filtered);
  }, [
    startDate,
    endDate,
    selectedTypes,
    selectedAssets,
    status,
    transactionId,
    allTransactions,
  ]);

  const uniqueTypes = [
    ...new Set(allTransactions.map((transaction) => transaction.type)),
  ];
  const uniqueAssets = [
    ...new Set(
      allTransactions.map(
        (transaction) => transaction.currency || transaction.asset
      )
    ),
  ];

  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const getCurrencyFlag = (currency) => {
    if (!currency) return null; // Ensure currency is defined
    const currencyData = currencyOptions.find(
      (option) => option.value === currency
    );
    return currencyData ? currencyData.flag : null;
  };

  const getAssetFlag = (asset) => {
    if (!asset) return null; // Ensure asset is defined
    const assetData = crptoOptionsFlag.find((option) => option.value === asset);
    return assetData ? assetData.flag : null;
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    const image = new Image();
    image.src = SignLogo;

    image.onload = () => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 14;
      const lineSpacing = 6;

      const renderHeader = () => {
        const headerHeight = 30;
        doc.setFillColor("#F0F0F0");
        doc.rect(0, 0, pageWidth, headerHeight, "F");

        const imageWidth = 50;
        const imageHeight = (imageWidth * image.height) / image.width;
        const logoX = leftMargin + 2;
        const logoY = (headerHeight - imageHeight) / 2;
        doc.addImage(image, "PNG", logoX, logoY, imageWidth, imageHeight);

        doc.setFontSize(16);
        doc.setTextColor("#0E2340");
        doc.text(
          "Premier Quantitative Strategies (PQS) SPC FUND",
          logoX + imageWidth + 10,
          logoY + 6
        );
        doc.setFontSize(12);
        doc.text(
          "21 Arlington St, London SW1A 1RD, United Kingdom",
          logoX + imageWidth + 10,
          logoY + 14
        );
      };

      const addFooter = () => {
        const footerText =
          "The Premier Quantitative Strategies SP Fund is our main investment vehicle which is an open-ended mutual fund based in the Cayman Islands and regulated by the Cayman Island Monetary Authority (CIMA). The Premier Quantitative Strategies (PQS) SP Fund is only available to institutional investors and high net worth individuals who can be classified as professional investors. The Offering Memorandum is the only authorized document for the offering of shares in the PQS SP FUND. Its distribution is subject to the particular laws and regulations of the jurisdictions in which a potential investor resides as well as the categorization of the potential investor as a professional client in accordance with FCA rules.";
        doc.setFontSize(8);
        doc.text(footerText, pageWidth / 2, pageHeight - 20, {
          align: "center",
          maxWidth: pageWidth - 40,
        });
      };

      const renderToAndPeriod = (sectionStartY) => {
        let yPosition = sectionStartY;

        yPosition += lineSpacing + 10;

        const todayDate = new Date().toLocaleDateString("en-GB");
        const calculatedStartDate =
          startDate ||
          (filteredTransactions.length > 0
            ? filteredTransactions[filteredTransactions.length - 1]?.date
            : "N/A");
        const calculatedEndDate =
          endDate ||
          (filteredTransactions.length > 0
            ? filteredTransactions[0]?.date
            : "N/A");

        const formattedStartDate =
          calculatedStartDate !== "N/A"
            ? new Date(calculatedStartDate).toLocaleDateString("en-GB")
            : "N/A";
        const formattedEndDate =
          calculatedEndDate !== "N/A"
            ? new Date(calculatedEndDate).toLocaleDateString("en-GB")
            : "N/A";

        const statementInfoX = pageWidth / 2 + 10;
        doc.text(`Statement Date: ${todayDate}`, statementInfoX, sectionStartY);
        doc.text(
          `Period Covered: ${formattedStartDate} - ${formattedEndDate}`,
          statementInfoX,
          sectionStartY + lineSpacing
        );

        // Separator Line
        doc.setDrawColor("#0E2340");
        doc.setLineWidth(0.5);
        const lineStartY = yPosition;
        doc.line(leftMargin, lineStartY, pageWidth - leftMargin, lineStartY);

        return yPosition + 10;
      };

      // Group transactions by asset or currency
      const transactionsByAsset = filteredTransactions.reduce((acc, txn) => {
        const asset = txn.currency || txn.asset;
        if (!acc[asset]) acc[asset] = [];
        acc[asset].push(txn);
        return acc;
      }, {});

      renderHeader();
      const sectionStartY = 50;
      let yPosition = renderToAndPeriod(sectionStartY);

      // Generate   Summary
      doc.setFont(undefined, "bold");
      doc.setFontSize(14);
      doc.text("  Summary", leftMargin, yPosition);
      doc.setFont(undefined, "normal");
      doc.setFontSize(12);
      yPosition += lineSpacing;

      // Create summary data
      const assetSummaryData = Object.keys(transactionsByAsset).map((asset) => {
        const transactions = transactionsByAsset[asset];
        const deposits = transactions
          .filter((txn) => txn.type === "Deposit")
          .reduce((sum, txn) => sum + txn.amount, 0);
        const withdrawals = transactions
          .filter((txn) => txn.type === "Withdrawal")
          .reduce((sum, txn) => sum + txn.amount, 0);
        const salesFees = transactions
          .filter((txn) => txn.type === "Sales Fee")
          .reduce((sum, txn) => sum + txn.amount, 0);
        const hedgingFees = transactions
          .filter((txn) => txn.type === "Hedging Fee")
          .reduce((sum, txn) => sum + txn.amount, 0);

        const totalFees = salesFees + hedgingFees;

        return [
          asset,
          `${deposits.toFixed(2)} ${asset}`,
          `${totalFees.toFixed(2)} ${asset}`,
          `${withdrawals.toFixed(2)} ${asset}`,
        ];
      });

      // Add summary table
      const headers = [
        ["Asset/Currency", "Deposits", "Total Fees", "Withdrawals"],
      ];
      doc.autoTable({
        startY: yPosition,
        head: headers,
        body: assetSummaryData,
        margin: { left: leftMargin },
        headStyles: {
          fillColor: null,
          textColor: "#0E2340",
          fontStyle: "bold",
        },
        bodyStyles: { fillColor: null, textColor: "#0E2340" },
      });

      yPosition = doc.lastAutoTable.finalY + lineSpacing + 20;

      // Add transactions for each asset
      Object.keys(transactionsByAsset).forEach((asset) => {
        doc.setFont(undefined, "bold");
        doc.setFontSize(14);
        doc.text(`Transactions for ${asset}`, leftMargin, yPosition);
        doc.setFont(undefined, "normal");
        doc.setFontSize(12);
        yPosition += lineSpacing;

        const transactions = transactionsByAsset[asset];

        doc.autoTable({
          head: [["Date", "Type", "Debit", "Credit", "Transaction ID"]],
          body: transactions.map((txn) => [
            new Date(txn.date).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            txn.type,
            txn.type !== "Deposit" ? `- ${txn.amount.toFixed(2)} ${asset}` : "",
            txn.type === "Deposit" ? `+ ${txn.amount.toFixed(2)} ${asset}` : "",
            txn._id,
          ]),
          startY: yPosition,
          styles: { fontSize: 10 },
          headStyles: {
            fillColor: "#0E2340",
            textColor: "#FFFFFF",
            fontStyle: "bold",
          },
          bodyStyles: { textColor: "#0E2340" },
        });

        yPosition = doc.lastAutoTable.finalY + 20;
      });

      addFooter();
      doc.save("transaction-report.pdf");
    };

    image.onerror = () => {
      console.error("Failed to load logo image.");
    };
  };

  return (
    <div className="wrapper d-flex align-items-stretch">
      {loading && <Loader />}
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="content" className="px-2">
        <button
          type="button"
          id="openSidebar"
          onClick={toggleSidebar}
          className="bars-btn"
        >
          <FaBarsStaggered />
        </button>
        <AdminNav />
        <div className="filters-main">
          <div className="date-filter-main">
            <div className="date-filter">
              <label className="bc-clr mb-3">Date Range</label>
              <div className="d-flex align-items-center gap-3">
                <input
                  type="date"
                  className="filter-inp w-50"
                  value={startDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="filter-inp w-50"
                  value={endDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <button className="clear-btn" onClick={clearDateFilters}>
                  Clear
                </button>
              </div>
            </div>
            <button className="download-pdf" onClick={() => exportToPdf()}>
              Download PDF
            </button>
          </div>

          <div className="other-filter mt-3">
            <div className="other-filter-sub">
              <label className="bc-clr">Type</label>
              <Select
                mode="multiple"
                placeholder="Select Type(s)"
                value={selectedTypes}
                onChange={setSelectedTypes}
              >
                {uniqueTypes.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="other-filter-sub">
              <label className="bc-clr">Asset/Currency</label>
              <Select
                mode="multiple"
                placeholder="Select Asset(s)"
                value={selectedAssets}
                onChange={setSelectedAssets}
              >
                {uniqueAssets.map((asset) => (
                  <Option key={asset} value={asset}>
                    {asset}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="other-filter-sub">
              <label className="bc-clr">Status</label>
              <select
                className="filter-inp"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="0">Pending</option>
                <option value="1">Completed</option>
                <option value="2">Failed</option>
              </select>
            </div>
            <div className="other-filter-sub">
              <label className="bc-clr">Transaction ID</label>
              <input
                type="text"
                className="filter-inp"
                placeholder="Search"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="tbl-main">
          <table className="simple-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Username</th>
                <th>Email</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Payment Method</th>
                <th>Asset / Currency</th>
                <th>Network</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction._id}>
                    <td style={{ width: "50px", wordWrap: "break-word" }}>
                      {transaction._id}
                    </td>
                    <td>
                      {new Date(transaction.date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                      , <br />{" "}
                      {new Date(transaction.date).toLocaleTimeString("en-GB")}
                    </td>
                    <td>{transaction.name}</td>
                    <td>{transaction.email}</td>
                    <td
                      className={
                        transaction.type === "Deposit"
                          ? "text-success"
                          : [
                              "Hedging Fee",
                              "Sales Fee",
                              "Management Fee",
                              "Performance Fee",
                              "Withdrawal",
                            ].includes(transaction.type)
                          ? "text-danger"
                          : ""
                      }
                    >
                      {transaction.type === "Deposit"
                        ? "+"
                        : ["Hedging Fee", "Sales Fee", "Withdrawal"].includes(
                            transaction.type
                          )
                        ? "-"
                        : ""}
                      {parseFloat(transaction.amount).toFixed(2)}{" "}
                      {transaction.currency}
                    </td>
                    <td>{transaction.type}</td>
                    <td className=" text-capitalize">
                      {transaction.paymentMethod}
                    </td>
                    <td>
                      {(() => {
                        const currencyFlag = getCurrencyFlag(
                          transaction.currency
                        );
                        const assetFlag = getAssetFlag(transaction.asset);
                        const flag = currencyFlag || assetFlag;

                        if (flag) {
                          return (
                            <img
                              src={flag}
                              alt={transaction.currency || transaction.asset}
                              style={{ width: "20px", marginRight: "5px" }}
                            />
                          );
                        }
                        return null;
                      })()}
                      {transaction.currency || transaction.asset}
                    </td>
                    <td> {transaction?.network || "-"} </td>
                    <td>
                      <button
                        className={`btn btn-sm ${
                          transaction.status === 0
                            ? "btn-warning"
                            : transaction.status === 1
                            ? "btn-success"
                            : transaction.status === 2
                            ? "btn-danger"
                            : ""
                        }`}
                      >
                        {transaction.status === 0
                          ? "Pending"
                          : transaction.status === 1
                          ? "Completed"
                          : transaction.status === 2
                          ? "Failed"
                          : "Unknown"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">No transactions found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
